import React, { useState, useEffect } from 'react';
import { api } from '@services/api';
import {
  Container,
  TextField,
  Typography,
  Grid,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import MuiAlert, { type Color } from '@material-ui/lab/Alert';
import { Upload } from '@components/Upload';
import { FileList } from '@components/FileList';
import { useFiles, FileProvider } from '@context/files';
import { Button } from '@components/Button';
import { Plus, Pencil, Trash, ArrowLeft } from '@phosphor-icons/react/dist/ssr';
import {
  DivBenefits,
  DivButtons,
  DivGridFieldDataContract,
  PaperContainer,
} from './styles';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import { useAuth } from '@context/auth';
import { AppError } from '@utils/AppError';
import { NavLink, useHistory, useLocation } from 'react-router-dom';

interface IRiscos {
  id_risco: string | number;
  nome: string;
}

// Interfaces corrigidas
interface IExameBase {
  id_exames: string | number;
  nome: string;
}

interface IExameFixo extends IExameBase {
  tipo: 'fixo';
  valor?: never;
}

interface IExameAdicional extends IExameBase {
  tipo: 'adicional';
  valor: number | string;
}

type IExame = IExameFixo | IExameAdicional;

interface IServico {
  id_servicos: string | number;
  nome: string;
  valor?: number | string;
}

interface IToxicologico {
  id_exame_tox: string | number;
  nome: string;
  valor: number | string;
}

interface IExameToxico {
  id_exame_tox: string | number;
  nome: string;
  valor?: number | string;
}

interface IPlano {
  id?: string;
  nome: string;
  valor: number | string;
  exames_fixos: IExameFixo[];
  exames_adicionais: IExameAdicional[];
  servicos: IServico[];
  exames_toxicologicos?: IToxicologico[];
  riscos?: IRiscos[];
}

interface IArquivoContrato {
  arquivo: string;
  cod_contrato: string;
  exibir: string;
  file_size: null | number;
  id_arquivo: string;
  id_arquivos_contrato: number;
  url: string;
}

interface IContrato {
  id_contrato: string;
  nome_contrato: string;
  cod_contrato: string;
  data_criacao: string;
  data_renovacao: string;
  num_conv_med?: string;
  num_conv_odont?: string;
  num_conv_acade?: string;
  num_seg_vida?: string;
  num_apol_pat?: string;
  tipo_plano?: string;
  valor_bronze?: string;
  valor_prata?: string;
  valor_ouro?: string;
  valor_platina?: string;
  valor_personalizado?: string;
  exames?: IExame[];
  riscos?: IRiscos[];
  servicos?: IServico[];
  exames_toxicos?: IExameToxico[];
  arquivos?: IArquivoContrato[];
}

// Schemas Zod corrigidos
const exameFixoSchema = z.object({
  id_exames: z.union([z.string(), z.number()]),
  nome: z.string(),
  tipo: z.literal('fixo')
});

const exameAdicionalSchema = z.object({
  id_exames: z.union([z.string(), z.number()]),
  nome: z.string(),
  valor: z.union([z.number(), z.string()]),
  tipo: z.literal('adicional')
});

const servicoSchema = z.object({
  id_servicos: z.union([z.string(), z.number()]),
  nome: z.string(),
  valor: z.union([z.number(), z.string()]).optional()
});

const toxicologicoSchema = z.object({
  id_exame_tox: z.union([z.string(), z.number()]),
  nome: z.string(),
  valor: z.union([z.number(), z.string()])
});

const riscosSchema = z.object({
  id_risco: z.union([z.string(), z.number()]),
  nome: z.string()
});

const planoShema = z.object({
  id: z.string().optional(),
  nome: z.string().min(1, 'Nome do plano é obrigatório'),
  valor: z.union([
    z.number().min(0, 'Valor deve ser maior ou igual a zero'),
    z.string().min(1, 'Valor é obrigatório')
  ]),
  exames_fixos: z.array(exameFixoSchema),
  exames_adicionais: z.array(exameAdicionalSchema),
  servicos: z.array(servicoSchema),
  exames_toxicologicos: z.array(toxicologicoSchema).optional(),
  riscos: z.array(riscosSchema).optional()
});

const contractShema = z.object({
  nome_contrato: z.string().trim().min(1, 'Nome do contrato é obrigatório'),
  cod_contrato: z.string().trim().min(1, 'Código do contrato é obrigatório'),
  data_criacao: z.string().min(1, 'Data de criação é obrigatória'),
  data_renovacao: z.string().min(1, 'Data de renovação é obrigatória'),
  arquivo: z.array(z.string()).optional(),
  planos: z.array(planoShema).min(1, 'Pelo menos um plano é obrigatório'),
  risco: z.array(riscosSchema).optional(),
  num_conv_med: z.string().optional(),
  num_conv_odont: z.string().optional(),
  num_conv_acade: z.string().optional(),
  num_seg_vida: z.string().optional(),
  num_apol_pat: z.string().optional(),
  usuario: z.string().optional()
});

export type ContractSaveFormData = z.infer<typeof contractShema>;
export type PlanoFormData = z.infer<typeof planoShema>;

const OPCOES_PLANOS = [
  { nome: 'Bronze', descricao: 'Plano básico com benefícios essenciais' },
  { nome: 'Prata', descricao: 'Plano intermediário com mais benefícios' },
  { nome: 'Ouro', descricao: 'Plano completo com benefícios premium' },
  { nome: 'Platina', descricao: 'Plano máximo com todos os benefícios' }
];

function useLazyFetch<T>(endpoint: string) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchData = async () => {
    if (loaded) return;
    
    setLoading(true);
    try {
      const res = await api.get(endpoint);
      setData(res.data);
      setLoaded(true);
    } catch (error) {
      console.error(`Erro ao carregar ${endpoint}:`, error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, fetchData, loaded };
}

// Modal para adicionar exames
function ExamesModal({ 
  open, 
  onClose, 
  onAddExame,
  tipo,
  examesJaAdicionados = []
}: { 
  open: boolean;
  onClose: () => void;
  onAddExame: (exame: IExameFixo | IExameAdicional) => void;
  tipo: 'fixo' | 'adicional';
  examesJaAdicionados?: (IExameFixo | IExameAdicional)[];
}) {
  const [selectedExame, setSelectedExame] = useState<IExameBase | null>(null);
  const [valorPersonalizado, setValorPersonalizado] = useState<string>('');
  const { data: examesDisponiveis, loading, fetchData } = useLazyFetch<IExameBase>('/ravim/exames');

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  useEffect(() => {
    if (open) {
      setSelectedExame(null);
      setValorPersonalizado('');
    }
  }, [open]);

  const examesFiltrados = examesDisponiveis.filter(exame => 
    !examesJaAdicionados.some(jaAdicionado => jaAdicionado.id_exames == exame.id_exames)
  );

  const handleAdd = () => {
    if (selectedExame) {
      if (examesJaAdicionados.some(jaAdicionado => jaAdicionado.id_exames == selectedExame.id_exames)) {
        alert('Este exame já foi adicionado anteriormente');
        return;
      }

      if (tipo === 'fixo') {
        const exameFixo: IExameFixo = {
          id_exames: selectedExame.id_exames,
          nome: selectedExame.nome,
          tipo: 'fixo'
        };
        onAddExame(exameFixo);
      } else {
        if (valorPersonalizado) {
          const exameAdicional: IExameAdicional = {
            id_exames: selectedExame.id_exames,
            nome: selectedExame.nome,
            valor: Number(valorPersonalizado),
            tipo: 'adicional'
          };
          onAddExame(exameAdicional);
        } else {
          alert('Por favor, informe o valor do exame adicional');
          return;
        }
      }
      onClose();
    } else {
      alert('Por favor, selecione um exame');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        Adicionar Exame {tipo === 'fixo' ? 'Fixo' : 'Adicional'}
      </DialogTitle>
      <DialogContent style={{ padding: '20px' }}>
        <Box style={{ marginBottom: '20px' }}>
          <Autocomplete
            options={examesFiltrados}
            loading={loading}
            getOptionLabel={(option: IExameBase) => `${option.id_exames} - ${option.nome}`}
            value={selectedExame}
            onChange={(event, newValue) => {
              setSelectedExame(newValue);
            }}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option =>
                option.id_exames.toString().toLowerCase().includes(inputValue) ||
                option.nome.toLowerCase().includes(inputValue)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar exame por código ou nome"
                variant="outlined"
                fullWidth
                placeholder="Digite o código ou nome do exame..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            noOptionsText={
              loading ? "Carregando..." : 
              "Nenhum exame disponível encontrado"
            }
          />
        </Box>
        
        {tipo === 'adicional' && selectedExame && (
          <Box>
            <TextField
              label="Valor do Exame Adicional"
              type="number"
              value={valorPersonalizado}
              onChange={(e) => setValorPersonalizado(e.target.value)}
              fullWidth
              placeholder="0.00"
              helperText="Informe o valor para este exame adicional"
              InputProps={{
                startAdornment: <Typography style={{ marginRight: 8 }}>R$</Typography>,
              }}
              inputProps={{
                step: "0.01",
                min: "0"
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button title="Cancelar" onClick={onClose} variantstyle="SECUNDARY" />
        <Button 
          title="Adicionar" 
          onClick={handleAdd}
          disabled={!selectedExame || (tipo === 'adicional' && !valorPersonalizado) || loading}
        />
      </DialogActions>
    </Dialog>
  );
}

// ServicosModal
function ServicosModal({ 
  open, 
  onClose, 
  onAddServico,
  servicosJaAdicionados = []
}: { 
  open: boolean;
  onClose: () => void;
  onAddServico: (servico: IServico) => void;
  servicosJaAdicionados?: IServico[];
}) {
  const [selectedServico, setSelectedServico] = useState<IServico | null>(null);
  const [valorPersonalizado, setValorPersonalizado] = useState<string>('');
  const { data: servicosDisponiveis, loading, fetchData } = useLazyFetch<IServico>('/ravim/servicos');

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  useEffect(() => {
    if (open) {
      setSelectedServico(null);
      setValorPersonalizado('');
    }
  }, [open]);

  const servicosFiltrados = servicosDisponiveis.filter(servico => 
    !servicosJaAdicionados.some(jaAdicionado => jaAdicionado.id_servicos == servico.id_servicos)
  );

  const handleAdd = () => {
    if (selectedServico) {
      if (servicosJaAdicionados.some(jaAdicionado => jaAdicionado.id_servicos == selectedServico.id_servicos)) {
        alert('Este serviço já foi adicionado anteriormente');
        return;
      }

      const servicoComValor: IServico = {
        id_servicos: selectedServico.id_servicos,
        nome: selectedServico.nome,
        valor: valorPersonalizado ? Number(valorPersonalizado) : undefined
      };
      onAddServico(servicoComValor);
      onClose();
    } else {
      alert('Por favor, selecione um serviço');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>Adicionar Serviço</DialogTitle>
      <DialogContent style={{ padding: '20px' }}>
        <Box style={{ marginBottom: '20px' }}>
          <Autocomplete
            options={servicosFiltrados}
            loading={loading}
            getOptionLabel={(option: IServico) => `${option.id_servicos} - ${option.nome}`}
            value={selectedServico}
            onChange={(event, newValue) => {
              setSelectedServico(newValue);
            }}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option =>
                option.id_servicos.toString().toLowerCase().includes(inputValue) ||
                option.nome.toLowerCase().includes(inputValue)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar serviço por código ou nome"
                variant="outlined"
                fullWidth
                placeholder="Digite o código ou nome do serviço..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            noOptionsText={
              loading ? "Carregando..." : 
              "Nenhum serviço disponível encontrado"
            }
          />
        </Box>
        
        <Box>
          <TextField
            label="Valor do Serviço (Opcional)"
            type="number"
            value={valorPersonalizado}
            onChange={(e) => setValorPersonalizado(e.target.value)}
            fullWidth
            placeholder="0.00"
            helperText="Informe o valor para este serviço (opcional)"
            InputProps={{
              startAdornment: <Typography style={{ marginRight: 8 }}>R$</Typography>,
            }}
            inputProps={{
              step: "0.01",
              min: "0"
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button title="Cancelar" onClick={onClose} variantstyle="SECUNDARY" />
        <Button 
          title="Adicionar" 
          onClick={handleAdd}
          disabled={!selectedServico || loading}
        />
      </DialogActions>
    </Dialog>
  );
}

// ToxicologicoModal
function ToxicologicoModal({ 
  open, 
  onClose, 
  onAddToxicologico,
  toxicologicosJaAdicionados = []
}: { 
  open: boolean;
  onClose: () => void;
  onAddToxicologico: (toxicologico: IToxicologico) => void;
  toxicologicosJaAdicionados?: IToxicologico[];
}) {
  const [selectedToxicologico, setSelectedToxicologico] = useState<IToxicologico | null>(null);
  const [valorPersonalizado, setValorPersonalizado] = useState<string>('');
  const { data: toxicologicosDisponiveis, loading, fetchData } = useLazyFetch<IToxicologico>('/ravim/exames-toxicologicos');

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  useEffect(() => {
    if (open) {
      setSelectedToxicologico(null);
      setValorPersonalizado('');
    }
  }, [open]);

  const toxicologicosFiltrados = toxicologicosDisponiveis.filter(toxico => 
    !toxicologicosJaAdicionados.some(jaAdicionado => jaAdicionado.id_exame_tox == toxico.id_exame_tox)
  );

  const handleAdd = () => {
    if (selectedToxicologico) {
      if (toxicologicosJaAdicionados.some(jaAdicionado => jaAdicionado.id_exame_tox == selectedToxicologico.id_exame_tox)) {
        alert('Este exame toxicológico já foi adicionado anteriormente');
        return;
      }

      if (!valorPersonalizado) {
        alert('Por favor, informe o valor do exame toxicológico');
        return;
      }

      const toxicologicoComValor: IToxicologico = {
        id_exame_tox: selectedToxicologico.id_exame_tox,
        nome: selectedToxicologico.nome,
        valor: Number(valorPersonalizado)
      };
      onAddToxicologico(toxicologicoComValor);
      onClose();
    } else {
      alert('Por favor, selecione um exame toxicológico');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>Adicionar Exame Toxicológico</DialogTitle>
      <DialogContent style={{ padding: '20px' }}>
        <Box style={{ marginBottom: '20px' }}>
          <Autocomplete
            options={toxicologicosFiltrados}
            loading={loading}
            getOptionLabel={(option: IToxicologico) => `${option.id_exame_tox} - ${option.nome}`}
            value={selectedToxicologico}
            onChange={(event, newValue) => {
              setSelectedToxicologico(newValue);
            }}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option =>
                option.id_exame_tox.toString().toLowerCase().includes(inputValue) ||
                option.nome.toLowerCase().includes(inputValue)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar exame toxicológico por código ou nome"
                variant="outlined"
                fullWidth
                placeholder="Digite o código ou nome do exame..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            noOptionsText={
              loading ? "Carregando..." : 
              "Nenhum exame toxicológico disponível encontrado"
            }
          />
        </Box>
        
        <Box>
          <TextField
            label="Valor do Exame Toxicológico"
            type="number"
            value={valorPersonalizado}
            onChange={(e) => setValorPersonalizado(e.target.value)}
            fullWidth
            placeholder="0.00"
            helperText="Informe o valor para este exame toxicológico"
            InputProps={{
              startAdornment: <Typography style={{ marginRight: 8 }}>R$</Typography>,
            }}
            inputProps={{
              step: "0.01",
              min: "0"
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button title="Cancelar" onClick={onClose} variantstyle="SECUNDARY" />
        <Button 
          title="Adicionar" 
          onClick={handleAdd}
          disabled={!selectedToxicologico || !valorPersonalizado || loading}
        />
      </DialogActions>
    </Dialog>
  );
}

// RiscosModal
function RiscosModal({ 
  open, 
  onClose, 
  onAddRiscos,
  riscosJaAdicionados = []
}: { 
  open: boolean;
  onClose: () => void;
  onAddRiscos: (riscos: IRiscos[]) => void;
  riscosJaAdicionados?: IRiscos[];
}) {
  const [selectedRiscos, setSelectedRiscos] = useState<IRiscos[]>([]);
  const { data: riscosDisponiveis, loading, fetchData } = useLazyFetch<IRiscos>('/ravim/riscos');

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  useEffect(() => {
    if (open) {
      setSelectedRiscos([]);
    }
  }, [open]);

  const riscosFiltrados = riscosDisponiveis.filter(risco => 
    !riscosJaAdicionados.some(jaAdicionado => jaAdicionado.id_risco == risco.id_risco)
  );

  const handleAdd = () => {
    if (selectedRiscos.length > 0) {
      onAddRiscos(selectedRiscos);
      onClose();
    } else {
      alert('Por favor, selecione pelo menos um risco');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>Adicionar Riscos</DialogTitle>
      <DialogContent style={{ padding: '20px' }}>
        <Box style={{ marginBottom: '20px' }}>
          <Autocomplete
            multiple
            options={riscosFiltrados}
            loading={loading}
            getOptionLabel={(option: IRiscos) => `${option.id_risco} - ${option.nome}`}
            value={selectedRiscos}
            onChange={(event, newValue) => {
              setSelectedRiscos(newValue);
            }}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option =>
                option.id_risco.toString().toLowerCase().includes(inputValue) ||
                option.nome.toLowerCase().includes(inputValue)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar riscos por código ou nome"
                variant="outlined"
                fullWidth
                placeholder="Digite o código ou nome do risco..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            noOptionsText={
              loading ? "Carregando..." : 
              "Nenhum risco disponível encontrado"
            }
          />
        </Box>
        
        {selectedRiscos.length > 0 && (
          <Box>
            <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>
              Riscos selecionados ({selectedRiscos.length}):
            </Typography>
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {selectedRiscos.map((risco, index) => (
                <Chip
                  key={index}
                  label={`${risco.id_risco} - ${risco.nome}`}
                  onDelete={() => {
                    setSelectedRiscos(prev => prev.filter(r => r.id_risco !== risco.id_risco));
                  }}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button title="Cancelar" onClick={onClose} variantstyle="SECUNDARY" />
        <Button 
          title={`Adicionar ${selectedRiscos.length} Riscos`} 
          onClick={handleAdd}
          disabled={selectedRiscos.length === 0 || loading}
        />
      </DialogActions>
    </Dialog>
  );
}

// PlanoCard
function PlanoCard({ 
  plano, 
  onEdit, 
  onDelete,
  dataCriacao,
  dataRenovacao
}: { 
  plano: PlanoFormData; 
  onEdit: () => void; 
  onDelete: () => void;
  dataCriacao?: string;
  dataRenovacao?: string;
}) {
  const calcularFidelidade = () => {
    if (!dataCriacao || !dataRenovacao) return 'Sem fidelidade';
    
    const hoje = new Date();
    const dataCriacaoObj = new Date(dataCriacao);
    const dataRenovacaoObj = new Date(dataRenovacao);
    
    if (hoje >= dataCriacaoObj && hoje <= dataRenovacaoObj) {
      return 'Em período de fidelidade';
    } else if (hoje > dataRenovacaoObj) {
      return 'Fidelidade concluída';
    } else {
      return 'Fidelidade pendente';
    }
  };

  const statusFidelidade = calcularFidelidade();

  const calcularValorTotal = () => {
    const valorBase = Number(plano.valor) || 0;
    const valorExamesAdicionais = (plano.exames_adicionais || []).reduce((total, exame) => 
      total + (Number(exame.valor) || 0), 0
    );
    const valorToxicologicos = (plano.exames_toxicologicos || []).reduce((total, toxico) => 
      total + (Number(toxico.valor) || 0), 0
    );

    return valorBase + valorExamesAdicionais + valorToxicologicos;
  };

  const valorTotal = calcularValorTotal();

  return (
    <Card 
      variant="outlined" 
      style={{ 
        border: '2px solid #e0e0e0',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent style={{ flex: 1, padding: '24px' }}>
        <Box style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Typography 
            variant="h5" 
            style={{ 
              fontWeight: 'bold',
              color: '#1976d2',
              marginBottom: '8px'
            }}
          >
            Plano {plano.nome}
          </Typography>
          <Typography 
            variant="body2" 
            color="textSecondary"
            style={{ marginBottom: '16px', minHeight: '40px' }}
          >
            {plano.nome === 'Bronze' && 'Plano básico com benefícios essenciais para sua empresa'}
            {plano.nome === 'Prata' && 'Plano intermediário com mais benefícios e coberturas'}
            {plano.nome === 'Ouro' && 'Plano completo com benefícios premium e atendimento especializado'}
            {plano.nome === 'Platina' && 'Plano máximo com todos os benefícios e coberturas exclusivas'}
          </Typography>
        </Box>

        <Box style={{ textAlign: 'center', marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <Typography 
            variant="h4" 
            style={{ 
              fontWeight: 'bold',
              color: '#1976d2'
            }}
          >
            R$ {valorTotal.toFixed(2)}
          </Typography>
          <Typography 
            variant="body2" 
            color="textSecondary"
          >
            Valor mensal total do plano
          </Typography>
        </Box>

        <Divider style={{ margin: '16px 0' }} />

        <Box style={{ marginBottom: '20px', textAlign: 'center' }}>
          <Typography 
            variant="subtitle2" 
            style={{ 
              fontWeight: 'bold',
              marginBottom: '8px',
              color: statusFidelidade === 'Em período de fidelidade' ? '#d32f2f' : '#2e7d32'
            }}
          >
            {statusFidelidade}
          </Typography>
          {dataCriacao && dataRenovacao && (
            <Typography variant="caption" color="textSecondary">
              De {new Date(dataCriacao).toLocaleDateString('pt-BR')} até {new Date(dataRenovacao).toLocaleDateString('pt-BR')}
            </Typography>
          )}
        </Box>

        <Box style={{ marginBottom: '20px' }}>
          <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '12px' }}>
            Benefícios Inclusos:
          </Typography>

          {plano.exames_fixos && plano.exames_fixos.length > 0 && (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                Exames Fixos ({plano.exames_fixos.length}):
              </Typography>
              {plano.exames_fixos.map((exame, index) => (
                <Box key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <Box style={{ 
                    width: '6px', 
                    height: '6px', 
                    backgroundColor: '#1976d2', 
                    borderRadius: '50%',
                    marginRight: '12px',
                    marginTop: '6px'
                  }} />
                  <Typography variant="body2" style={{ lineHeight: 1.4 }}>
                    {exame.nome}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {plano.exames_adicionais && plano.exames_adicionais.length > 0 && (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                Exames Adicionais ({plano.exames_adicionais.length}):
              </Typography>
              {plano.exames_adicionais.map((exame, index) => (
                <Box key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <Box style={{ 
                    width: '6px', 
                    height: '6px', 
                    backgroundColor: '#1976d2', 
                    borderRadius: '50%',
                    marginRight: '12px',
                    marginTop: '6px'
                  }} />
                  <Typography variant="body2" style={{ lineHeight: 1.4 }}>
                    {exame.nome} - R$ {Number(exame.valor).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {plano.servicos && plano.servicos.length > 0 && (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                Serviços ({plano.servicos.length}):
              </Typography>
              {plano.servicos.map((servico, index) => (
                <Box key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <Box style={{ 
                    width: '6px', 
                    height: '6px', 
                    backgroundColor: '#1976d2', 
                    borderRadius: '50%',
                    marginRight: '12px',
                    marginTop: '6px'
                  }} />
                  <Typography variant="body2" style={{ lineHeight: 1.4 }}>
                    {servico.nome}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {plano.exames_toxicologicos && plano.exames_toxicologicos.length > 0 && (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                Exames Toxicológicos ({plano.exames_toxicologicos.length}):
              </Typography>
              {plano.exames_toxicologicos.map((toxico, index) => (
                <Box key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <Box style={{ 
                    width: '6px', 
                    height: '6px', 
                    backgroundColor: '#1976d2', 
                    borderRadius: '50%',
                    marginRight: '12px',
                    marginTop: '6px'
                  }} />
                  <Typography variant="body2" style={{ lineHeight: 1.4 }}>
                    {toxico.nome} - R$ {Number(toxico.valor).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {plano.riscos && plano.riscos.length > 0 && (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                Riscos Cobertos ({plano.riscos.length}):
              </Typography>
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {plano.riscos.map((risco, index) => (
                  <Chip 
                    key={index}
                    label={risco.nome}
                    size="small"
                    variant="outlined"
                    style={{ margin: '2px', backgroundColor: '#e3f2fd' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              Total de Itens:
            </Typography>
            <Typography variant="body2">
              {(plano.exames_fixos?.length || 0) + (plano.exames_adicionais?.length || 0) + (plano.servicos?.length || 0) + (plano.exames_toxicologicos?.length || 0)}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions style={{ padding: '16px 24px', justifyContent: 'flex-end', borderTop: '1px solid #e0e0e0' }}>
        <Tooltip title="Editar Plano">
          <IconButton 
            size="small" 
            onClick={onEdit}
            style={{ marginRight: '8px', color: '#1976d2' }}
          >
            <Pencil size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir Plano">
          <IconButton 
            size="small" 
            onClick={onDelete}
            style={{ color: '#d32f2f' }}
          >
            <Trash size={16} />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

// PlanoModal
function PlanoModal({ 
  open, 
  onClose, 
  onSave, 
  plano
}: { 
  open: boolean;
  onClose: () => void;
  onSave: (plano: PlanoFormData) => void;
  plano?: PlanoFormData;
}) {
  const [examesDialog, setExamesDialog] = useState(false);
  const [servicosDialog, setServicosDialog] = useState(false);
  const [toxicologicoDialog, setToxicologicoDialog] = useState(false);
  const [riscosDialog, setRiscosDialog] = useState(false);
  const [tipoExame, setTipoExame] = useState<'fixo' | 'adicional'>('fixo');

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<PlanoFormData>({
    resolver: zodResolver(planoShema),
    defaultValues: {
      nome: 'Bronze',
      valor: '',
      exames_fixos: [],
      exames_adicionais: [],
      servicos: [],
      exames_toxicologicos: [],
      riscos: []
    }
  });

  const { 
    fields: examesFixosFields, 
    append: appendExameFixo, 
    remove: removeExameFixo,
    replace: replaceExamesFixos
  } = useFieldArray({
    control,
    name: 'exames_fixos'
  });

  const { 
    fields: examesAdicionaisFields, 
    append: appendExameAdicional, 
    remove: removeExameAdicional,
    replace: replaceExamesAdicionais
  } = useFieldArray({
    control,
    name: 'exames_adicionais'
  });

  const { 
    fields: servicosFields, 
    append: appendServico, 
    remove: removeServico,
    replace: replaceServicos
  } = useFieldArray({
    control,
    name: 'servicos'
  });

  const { 
    fields: toxicologicosFields, 
    append: appendToxicologico, 
    remove: removeToxicologico,
    replace: replaceToxicologicos
  } = useFieldArray({
    control,
    name: 'exames_toxicologicos'
  });

  const { 
    fields: riscosFields, 
    append: appendRisco, 
    remove: removeRisco,
    replace: replaceRiscos
  } = useFieldArray({
    control,
    name: 'riscos'
  });

  // Carregar dados quando o modal abrir
  useEffect(() => {
    if (open && plano) {
      reset({
        nome: plano.nome || 'Bronze',
        valor: plano.valor || '',
        exames_fixos: plano.exames_fixos || [],
        exames_adicionais: plano.exames_adicionais || [],
        servicos: plano.servicos || [],
        exames_toxicologicos: plano.exames_toxicologicos || [],
        riscos: plano.riscos || []
      });
    } else if (open) {
      reset({
        nome: 'Bronze',
        valor: '',
        exames_fixos: [],
        exames_adicionais: [],
        servicos: [],
        exames_toxicologicos: [],
        riscos: []
      });
    }
  }, [open, plano, reset]);

  const handleAddExame = (exame: IExameFixo | IExameAdicional) => {
    if (exame.tipo === 'fixo') {
      appendExameFixo(exame);
    } else {
      appendExameAdicional(exame);
    }
    setExamesDialog(false);
  };

  const handleAddServico = (servico: IServico) => {
    appendServico(servico);
    setServicosDialog(false);
  };

  const handleAddToxicologico = (toxico: IToxicologico) => {
    appendToxicologico(toxico);
    setToxicologicoDialog(false);
  };

  const handleAddRiscos = (novosRiscos: IRiscos[]) => {
    novosRiscos.forEach(risco => {
      appendRisco(risco);
    });
    setRiscosDialog(false);
  };

  const calcularValorTotal = () => {
    const valorTotal = Number(watch('valor')) || 0;
    const examesAdicionais = watch('exames_adicionais') || [];
    const toxicologicos = watch('exames_toxicologicos') || [];

    const valorExamesAdicionais = examesAdicionais.reduce((total, exame) => 
      total + (Number(exame.valor) || 0), 0
    );
    const valorToxicologicos = toxicologicos.reduce((total, toxico) => 
      total + (Number(toxico.valor) || 0), 0
    );

    return {
      base: valorTotal,
      examesAdicionais: valorExamesAdicionais,
      toxicologicos: valorToxicologicos,
      final: valorTotal + valorExamesAdicionais + valorToxicologicos
    };
  };

  const valores = calcularValorTotal();

  const handleSave = (planoData: PlanoFormData) => {
    onSave(planoData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {plano ? `Editar Plano - ${plano.nome}` : 'Adicionar Plano'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Preview do Plano */}
          <Grid item xs={12}>
            <Card variant="outlined" style={{ backgroundColor: '#f8f9fa', marginBottom: '16px' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resumo do Plano {watch('nome')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Typography variant="body2" style={{ fontWeight: 'bold', marginRight: '8px', minWidth: '100px' }}>
                        Valor Base:
                      </Typography>
                      <Typography variant="body2">R$ {valores.base.toFixed(2)}</Typography>
                    </Box>
                    <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Typography variant="body2" style={{ fontWeight: 'bold', marginRight: '8px', minWidth: '100px' }}>
                        Valor Total:
                      </Typography>
                      <Typography variant="body2" style={{ color: '#1976d2', fontWeight: 'bold' }}>
                        R$ {valores.final.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Typography variant="body2" style={{ fontWeight: 'bold', marginRight: '8px', minWidth: '100px' }}>
                        Itens Inclusos:
                      </Typography>
                      <Typography variant="body2">
                        {examesFixosFields.length + examesAdicionaisFields.length + servicosFields.length + toxicologicosFields.length + riscosFields.length}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Nome e Valor do Plano */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Nome do Plano</InputLabel>
              <Select
                {...register('nome')}
                value={watch('nome')}
                label="Nome do Plano"
              >
                {OPCOES_PLANOS.map((opcao) => (
                  <MenuItem key={opcao.nome} value={opcao.nome}>
                    {opcao.nome} - {opcao.descricao}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Valor Base do Plano"
              type="number"
              {...register('valor', { valueAsNumber: true })}
              value={watch('valor')}
              error={!!errors.valor}
              helperText={errors.valor?.message}
              margin="normal"
              InputProps={{
                startAdornment: <Typography style={{ marginRight: 8 }}>R$</Typography>,
              }}
            />
          </Grid>

          {/* Seções de Exames, Serviços, Toxicológicos e Riscos */}
          <Grid item xs={12}>
            <Typography variant="h6" style={{ marginBottom: '16px', marginTop: '24px' }}>
              Configuração do Plano
            </Typography>
          </Grid>

          {/* Exames Fixos */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" style={{ marginBottom: '16px' }}>
              <CardContent>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    Exames Fixos
                  </Typography>
                  <Button
                    title="Adicionar"
                    size="small"
                    onClick={() => {
                      setTipoExame('fixo');
                      setExamesDialog(true);
                    }}
                  />
                </Box>
                {examesFixosFields.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Nenhum exame fixo adicionado
                  </Typography>
                ) : (
                  <Box>
                    {examesFixosFields.map((exame, index) => (
                      <Box key={exame.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <Typography variant="body2">{exame.nome}</Typography>
                        <IconButton size="small" onClick={() => removeExameFixo(index)} style={{ color: '#d32f2f' }}>
                          <Trash size={16} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Exames Adicionais */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" style={{ marginBottom: '16px' }}>
              <CardContent>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    Exames Adicionais
                  </Typography>
                  <Button
                    title="Adicionar"
                    size="small"
                    onClick={() => {
                      setTipoExame('adicional');
                      setExamesDialog(true);
                    }}
                  />
                </Box>
                {examesAdicionaisFields.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Nenhum exame adicional adicionado
                  </Typography>
                ) : (
                  <Box>
                    {examesAdicionaisFields.map((exame, index) => (
                      <Box key={exame.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <Box>
                          <Typography variant="body2">{exame.nome}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            R$ {Number(exame.valor).toFixed(2)}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => removeExameAdicional(index)} style={{ color: '#d32f2f' }}>
                          <Trash size={16} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Serviços */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" style={{ marginBottom: '16px' }}>
              <CardContent>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    Serviços
                  </Typography>
                  <Button
                    title="Adicionar"
                    size="small"
                    onClick={() => setServicosDialog(true)}
                  />
                </Box>
                {servicosFields.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Nenhum serviço adicionado
                  </Typography>
                ) : (
                  <Box>
                    {servicosFields.map((servico, index) => (
                      <Box key={servico.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <Box>
                          <Typography variant="body2">{servico.nome}</Typography>
                          {servico.valor && (
                            <Typography variant="caption" color="textSecondary">
                              R$ {Number(servico.valor).toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                        <IconButton size="small" onClick={() => removeServico(index)} style={{ color: '#d32f2f' }}>
                          <Trash size={16} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Exames Toxicológicos */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" style={{ marginBottom: '16px' }}>
              <CardContent>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    Exames Toxicológicos
                  </Typography>
                  <Button
                    title="Adicionar"
                    size="small"
                    onClick={() => setToxicologicoDialog(true)}
                  />
                </Box>
                {toxicologicosFields.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Nenhum exame toxicológico adicionado
                  </Typography>
                ) : (
                  <Box>
                    {toxicologicosFields.map((toxico, index) => (
                      <Box key={toxico.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <Box>
                          <Typography variant="body2">{toxico.nome}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            R$ {Number(toxico.valor).toFixed(2)}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => removeToxicologico(index)} style={{ color: '#d32f2f' }}>
                          <Trash size={16} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Riscos */}
          <Grid item xs={12}>
            <Card variant="outlined" style={{ marginBottom: '16px' }}>
              <CardContent>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    Riscos Cobertos
                  </Typography>
                  <Button
                    title="Adicionar"
                    size="small"
                    onClick={() => setRiscosDialog(true)}
                  />
                </Box>
                {riscosFields.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Nenhum risco adicionado
                  </Typography>
                ) : (
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {riscosFields.map((risco, index) => (
                      <Chip
                        key={risco.id}
                        label={risco.nome}
                        onDelete={() => removeRisco(index)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Modais */}
        <ExamesModal
          open={examesDialog}
          onClose={() => setExamesDialog(false)}
          onAddExame={handleAddExame}
          tipo={tipoExame}
          examesJaAdicionados={[...examesFixosFields, ...examesAdicionaisFields]}
        />

        <ServicosModal
          open={servicosDialog}
          onClose={() => setServicosDialog(false)}
          onAddServico={handleAddServico}
          servicosJaAdicionados={servicosFields}
        />

        <ToxicologicoModal
          open={toxicologicoDialog}
          onClose={() => setToxicologicoDialog(false)}
          onAddToxicologico={handleAddToxicologico}
          toxicologicosJaAdicionados={toxicologicosFields}
        />

        <RiscosModal
          open={riscosDialog}
          onClose={() => setRiscosDialog(false)}
          onAddRiscos={handleAddRiscos}
          riscosJaAdicionados={riscosFields}
        />

      </DialogContent>
      <DialogActions>
        <Button title="Cancelar" onClick={onClose} variantstyle="SECUNDARY" />
        <Button 
          title="Salvar Plano" 
          onClick={handleSubmit(handleSave)}
        />
      </DialogActions>
    </Dialog>
  );
}

// Componente principal EditarContratoEmpresa
export const EditarContratoEmpresa = () => {
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const contratoData = location.state as IContrato;

  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<Color | undefined>();
  const [loading, setLoading] = useState(false);

  const [planoModalOpen, setPlanoModalOpen] = useState(false);
  const [planoEditando, setPlanoEditando] = useState<(PlanoFormData & { _index?: number }) | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitting, isValid }
  } = useForm<ContractSaveFormData>({
    resolver: zodResolver(contractShema),
    defaultValues: {
      usuario: `${user?.nome} - ${new Date().toLocaleString()}`,
      planos: [],
      risco: []
    }
  });

  const { fields: planosFields, append: appendPlano, remove: removePlano, update: updatePlano } = useFieldArray({
    control,
    name: 'planos'
  });

  const {
    uploadedFiles: files,
    fetchDataFiles,
    receivedCodContrato
  } = useFiles();

  // Carregar dados do contrato - ATUALIZADO para a estrutura real
  useEffect(() => {
    const fetchContratoData = async () => {
      try {
        setLoading(true);
        const res = await api.post(`/ravim/dados/${contratoData.cod_contrato}`);
        const data = res.data;

        if (!data) {
          throw new Error('Dados do contrato não encontrados');
        }

        const contratoInfo = data.contrato || {};
        const beneficios = data.beneficios?.[0] || {};

        // Transformar dados para o formato do formulário
        const contratoFormatado = {
          nome_contrato: contratoInfo.nome_contrato || contratoData.nome_contrato,
          cod_contrato: contratoData.cod_contrato,
          data_criacao: contratoInfo.data_criacao || contratoData.data_criacao,
          data_renovacao: contratoInfo.data_renovacao || contratoData.data_renovacao,
          num_conv_med: beneficios.num_conv_med || contratoData.num_conv_med,
          num_conv_odont: beneficios.num_conv_odont || contratoData.num_conv_odont,
          num_conv_acade: beneficios.num_conv_acade || contratoData.num_conv_acade,
          num_seg_vida: beneficios.num_seg_vida || contratoData.num_seg_vida,
          num_apol_pat: beneficios.num_apol_pat || contratoData.num_apol_pat,
          usuario: `${user?.nome} - ${new Date().toLocaleString()}`,
          arquivo: [],
          planos: [],
          risco: data.riscos?.map((risco: any) => ({
            id_risco: risco.id_risco_contr_empresa?.toString() || risco.id_risco,
            nome: risco.nome
          })) || []
        };

        reset(contratoFormatado);

        // SEPARAR EXAMES FIXOS E ADICIONAIS baseado no tipo_exame
        const examesFixos: IExameFixo[] = [];
        const examesAdicionais: IExameAdicional[] = [];

        data.exames?.forEach((exame: any) => {
          if (exame.tipo_exame === 'fixo') {
            examesFixos.push({
              id_exames: exame.id_exame_contr_empresa?.toString() || exame.id_exames,
              nome: exame.nome,
              tipo: 'fixo' as const
            });
          } else if (exame.tipo_exame === 'adicional') {
            examesAdicionais.push({
              id_exames: exame.id_exame_contr_empresa?.toString() || exame.id_exames,
              nome: exame.nome,
              valor: Number(exame.valor) || 0,
              tipo: 'adicional' as const
            });
          }
        });

        // Configurar plano com os dados reais
        const plano: PlanoFormData = {
          nome: contratoInfo.plano_bronze || contratoInfo.plano_prata || contratoInfo.plano_ouro || contratoInfo.plano_platina || 'Bronze',
          valor: contratoInfo.valor_bronze || contratoInfo.valor_prata || contratoInfo.valor_ouro || contratoInfo.valor_platina || contratoInfo.valor_personalizado || '0',
          exames_fixos: examesFixos,
          exames_adicionais: examesAdicionais,
          servicos: data.servicos?.map((servico: any) => ({
            id_servicos: servico.id_servico_contr_empresa?.toString() || servico.id_servicos,
            nome: servico.nome,
            valor: Number(servico.valor) || 0
          })) || [],
          exames_toxicologicos: data.exame_toxi?.map((exame: any) => ({
            id_exame_tox: exame.id_exame_toxi_contr_empresa?.toString() || exame.id_exame_tox,
            nome: exame.nome,
            valor: Number(exame.valor) || 0
          })) || [],
          riscos: data.riscos?.map((risco: any) => ({
            id_risco: risco.id_risco_contr_empresa?.toString() || risco.id_risco,
            nome: risco.nome
          })) || []
        };

        appendPlano(plano);

        // Configurar arquivos
        receivedCodContrato(contratoData.cod_contrato);
        if (data.arquivos && Array.isArray(data.arquivos)) {
          const formattedFiles = data.arquivos.map(
            (arquivo: IArquivoContrato) => ({
              id: arquivo.id_arquivo,
              name: arquivo.arquivo.split('_').slice(1).join('_'),
              uploaded: true,
              url: arquivo.url,
              progress: 100,
              file: null,
              cod_contrato: arquivo.cod_contrato
            })
          );
          fetchDataFiles(formattedFiles);
        }

      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        setSnack(true);
        setSeverity('error');
        setMessage('Erro ao carregar dados do contrato');
      } finally {
        setLoading(false);
      }
    };

    fetchContratoData();
  }, [contratoData.cod_contrato]);

  const filesNames = files.map((file) => file.name);

  useEffect(() => {
    if (filesNames) {
      setValue('arquivo', filesNames);
    }
  }, [files]);

  useEffect(() => {
    receivedCodContrato(getValues('cod_contrato'));
  }, [getValues('cod_contrato')]);

  const handleOpenPlanoModal = (planoIndex?: number) => {
    if (planoIndex !== undefined) {
      setPlanoEditando({ 
        ...planosFields[planoIndex],
        _index: planoIndex
      });
    } else {
      setPlanoEditando(null);
    }
    setPlanoModalOpen(true);
  };

  const handleSavePlano = (planoData: PlanoFormData) => {
    if (planoEditando && planoEditando._index !== undefined) {
      updatePlano(planoEditando._index, planoData);
    } else {
      appendPlano(planoData);
    }
    setPlanoModalOpen(false);
    setPlanoEditando(null);
  };

  const handleDeletePlano = (index: number) => {
    removePlano(index);
  };

  // Função para salvar contrato - ATUALIZADA para a estrutura correta
  async function atualizarContrato(data: ContractSaveFormData) {
    try {
      setLoading(true);

      const arquivosEnviados = files
        .filter((file) => file.uploaded)
        .map((file) => ({
          arquivo: file.name,
          url: file.url,
          id_arquivo: file.id
        }));

      // Preparar dados no formato esperado pelo backend
      const plano = data.planos[0];

      const dadosParaEnviar = {
        nome_contrato: data.nome_contrato,
        cod_contrato: data.cod_contrato,
        data_criacao: data.data_criacao,
        data_renovacao: data.data_renovacao,
        num_conv_med: data.num_conv_med,
        num_conv_odont: data.num_conv_odont,
        num_conv_acade: data.num_conv_acade,
        num_seg_vida: data.num_seg_vida,
        num_apol_pat: data.num_apol_pat,
        tipo_plano: plano.nome,
        valor_bronze: plano.nome === 'Bronze' ? String(plano.valor) : '0',
        valor_prata: plano.nome === 'Prata' ? String(plano.valor) : '0',
        valor_ouro: plano.nome === 'Ouro' ? String(plano.valor) : '0',
        valor_platina: plano.nome === 'Platina' ? String(plano.valor) : '0',
        valor_personalizado: plano.nome === 'Personalizado' ? String(plano.valor) : '0',
        arquivos: [
          ...(contratoData.arquivos || []),
          ...arquivosEnviados
        ],
        usuario: data.usuario,
        exames: [
          // Exames fixos (sem valor)
          ...plano.exames_fixos.map((exame) => ({
            id_exame_contr_empresa: data.cod_contrato,
            nome: exame.nome,
            tipo_exame: 'fixo',
            valor: '0'
          })),
          // Exames adicionais (com valor)
          ...plano.exames_adicionais.map((exame) => ({
            id_exame_contr_empresa: data.cod_contrato,
            nome: exame.nome,
            tipo_exame: 'adicional',
            valor: String(exame.valor)
          }))
        ],
        riscos: plano.riscos?.map((risco) => ({
          id_risco_contr_empresa: data.cod_contrato,
          nome: risco.nome
        })) || [],
        servicos: plano.servicos?.map((servico) => ({
          id_servico_contr_empresa: data.cod_contrato,
          nome: servico.nome,
          valor: String(servico.valor || '0')
        })) || [],
        exames_toxicos: plano.exames_toxicologicos?.map((exame) => ({
          id_exame_tox: data.cod_contrato,
          nome: exame.nome,
          valor: String(exame.valor)
        })) || []
      };

      const res = await api.put('/ravim/update', dadosParaEnviar);

      setSnack(true);
      setSeverity('success');
      setMessage('Contrato atualizado com sucesso!');

      setTimeout(() => {
        history.push('/contratos-empresa');
      }, 2000);
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      setSnack(true);
      setSeverity('error');
      setMessage('Erro ao atualizar contrato');
    } finally {
      setLoading(false);
    }
  }

  const dataCriacao = watch('data_criacao');
  const dataRenovacao = watch('data_renovacao');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <Typography variant="h4" align="center" style={{ marginBottom: '32px', fontWeight: 'bold' }}>
        Editar Contrato - {contratoData.nome_contrato}
      </Typography>
      
      <form onSubmit={handleSubmit(atualizarContrato)}>
        <PaperContainer>
          <Typography variant="h5" style={{ marginBottom: '16px', color: '#1976d2' }}>
            Dados do Cadastro
          </Typography>
          
          <Grid container spacing={1}>
            <Grid item xs>
              <DivGridFieldDataContract>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Nome do contrato"
                  {...register('nome_contrato')}
                  error={!!errors.nome_contrato}
                  helperText={errors.nome_contrato?.message}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Código do Contrato"
                  {...register('cod_contrato')}
                  error={!!errors.cod_contrato}
                  helperText={errors.cod_contrato?.message}
                  disabled
                />

                <TextField
                  label="Data de Criação"
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  {...register('data_criacao')}
                  error={!!errors.data_criacao}
                  helperText={errors.data_criacao?.message}
                />
                <TextField
                  label="Data de Renovação"
                  type="date"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  {...register('data_renovacao')}
                  error={!!errors.data_renovacao}
                  helperText={errors.data_renovacao?.message}
                />
              </DivGridFieldDataContract>

              <Upload />
              <FileList />
            </Grid>
          </Grid>

          <Typography variant="h5" style={{ marginBottom: '16px', marginTop: '24px', color: '#1976d2' }}>
            Benefícios
          </Typography>

          <DivBenefits>
            <TextField
              fullWidth
              variant="outlined"
              label="Nº Convênio Médico"
              {...register('num_conv_med')}
            />
            <TextField
              fullWidth
              variant="outlined"
              label="Nº Convênio Odontológico"
              {...register('num_conv_odont')}
            />
            <TextField
              fullWidth
              variant="outlined"
              label="Nº Convênio Academia"
              {...register('num_conv_acade')}
            />
            <TextField
              fullWidth
              variant="outlined"
              label="Nº Seguro de Vida"
              {...register('num_seg_vida')}
            />
            <TextField
              fullWidth
              variant="outlined"
              label="Nº Apólice PAT"
              {...register('num_apol_pat')}
            />
          </DivBenefits>

          <Typography variant="h5" style={{ marginBottom: '16px', marginTop: '24px', color: '#1976d2' }}>
            Plano do Contrato
          </Typography>
          
          <Box style={{ marginBottom: 24 }}>
            <Button
              title={planosFields.length === 0 ? "Adicionar Plano" : "Editar Plano"}
              onClick={() => handleOpenPlanoModal()}
              icon={<Plus size={20} />}
              type="button"
              disabled={planosFields.length > 0 && !planoEditando}
            />
            {planosFields.length > 0 && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                O contrato pode ter apenas um plano. Edite o plano existente ou exclua para criar um novo.
              </Typography>
            )}
          </Box>

          <Grid container spacing={3}>
            {planosFields.map((plano, index) => (
              <Grid item xs={12} key={plano.id}>
                <PlanoCard
                  plano={plano}
                  onEdit={() => handleOpenPlanoModal(index)}
                  onDelete={() => handleDeletePlano(index)}
                  dataCriacao={dataCriacao}
                  dataRenovacao={dataRenovacao}
                />
              </Grid>
            ))}
            {planosFields.length === 0 && (
              <Grid item xs={12}>
                <Card variant="outlined" style={{ textAlign: 'center', padding: '40px' }}>
                  <Typography variant="h6" color="textSecondary">
                    Nenhum plano cadastrado
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                    Clique em "Adicionar Plano" para criar o plano do contrato
                  </Typography>
                </Card>
              </Grid>
            )}
          </Grid>
        </PaperContainer>

        <DivButtons>
          <NavLink to="/contratos-empresa">
            <Button title="Voltar" variantstyle="SECUNDARY" icon={<ArrowLeft size={20} />} />
          </NavLink>
          <Button
            type="submit"
            title="Atualizar Contrato"
            isLoading={isSubmitting}
            disabled={!isValid || planosFields.length === 0}
          />
        </DivButtons>
      </form>

      <PlanoModal
        open={planoModalOpen}
        onClose={() => {
          setPlanoModalOpen(false);
          setPlanoEditando(null);
        }}
        onSave={handleSavePlano}
        plano={planoEditando || undefined}
      />

      <Snackbar
        open={snack}
        autoHideDuration={6000}
        onClose={() => setSnack(false)}
      >
        <MuiAlert
          severity={severity}
          variant="filled"
          onClose={() => setSnack(false)}
        >
          {message}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};

// Componente wrapper para o FileProvider
export const EditarContratoEmpresaWrapper = () => {
  return (
    <FileProvider>
      <EditarContratoEmpresa />
    </FileProvider>
  );
};